import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { ComparisonMetric } from '../../utils/comparison/alignStreams';
import type { LapOverlaySeries } from '../../utils/comparison/lapOverlaySeries';
import { useChartScrub } from '../../hooks/useChartScrub';
import {
  buildMultiLapChartLayout,
  chartXFromDataX,
  dataXFromClientX,
  formatChartX,
  formatMetricValue,
  sampleSeriesAtDataX,
} from './chartHoverUtils';
import { ChartHoverHeader, ChartHoverRow, ChartHoverTooltip } from './ChartHoverTooltip';

interface MultiLapOverlayChartProps {
  running?: boolean;
  series: LapOverlaySeries[];
  metric: ComparisonMetric;
  height?: number;
}

const METRIC_LABELS: Record<ComparisonMetric, string> = {
  power: 'Power (W)',
  heart_rate: 'Heart Rate (bpm)',
  cadence: 'Cadence (rpm)',
  speed: 'Speed (km/h)',
  altitude: 'Elevation (m)',
};

const METRIC_UNITS: Record<ComparisonMetric, string> = {
  power: 'W',
  heart_rate: 'bpm',
  cadence: 'rpm',
  speed: 'km/h',
  altitude: 'm',
};

export function MultiLapOverlayChart({ series, metric, height = 280, running = false }: MultiLapOverlayChartProps) {
  const { t } = useTranslation();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hoverDataX, setHoverDataX] = useState<number | null>(null);

  const layout = useMemo(() => buildMultiLapChartLayout(series, height), [series, height]);

  const paths = useMemo(() => {
    const { ml, mt, plotW, plotH, xMax, yMax } = layout;
    return series.map(s => {
      let d = '';
      s.points.forEach((p, i) => {
        const x = ml + (p.x / xMax) * plotW;
        const y = mt + plotH - (p.y / yMax) * plotH;
        d += i === 0 ? `M${x},${y}` : ` L${x},${y}`;
      });
      return { d, color: s.color, label: s.label, id: s.id };
    });
  }, [series, layout]);

  const updateHoverAtX = useCallback(
    (clientX: number) => {
      const svg = wrapRef.current?.querySelector('svg');
      if (!svg) return;
      const dataX = dataXFromClientX(svg, clientX, layout);
      setHoverDataX(dataX);
    },
    [layout],
  );

  const scrub = useChartScrub(true, updateHoverAtX, () => setHoverDataX(null));

  const hoverSamples = hoverDataX != null ? sampleSeriesAtDataX(series, hoverDataX) : [];
  const hoverLineX = hoverDataX != null ? chartXFromDataX(hoverDataX, layout) : null;

  const tooltipPos = useMemo(() => {
    if (hoverLineX == null || !wrapRef.current) return null;
    const w = wrapRef.current.clientWidth;
    const scale = w / 800;
    const px = hoverLineX * scale;
    const tooltipW = 190;
    const left = px + 14 + tooltipW > w ? px - tooltipW - 14 : px + 14;
    return { left: Math.max(4, left), top: 28 };
  }, [hoverLineX]);

  if (series.length < 2) return null;

  return (
    <div style={{ ...chartWrap, height, marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-3)', marginBottom: 4 }}>
        Multi-lap overlay — {metric === 'cadence' && running ? `${t('runningMetrics.cadence')} (${t('runningMetrics.cadenceUnit')})` : METRIC_LABELS[metric]} (aligned from lap start)
      </div>
      <div ref={wrapRef} className="comparison-chart-touch" style={{ position: 'relative' }}>
        <svg
          viewBox={`0 0 800 ${height}`}
          width="100%"
          height={height - 24}
          preserveAspectRatio="none"
          {...scrub}
          style={{ display: 'block' }}
        >
          <rect x={0} y={0} width={800} height={height} fill="var(--palette-sunken)" rx={8} />
          {paths.map(p => (
            <path key={p.id} d={p.d} fill="none" stroke={p.color} strokeWidth={2.2} opacity={0.92} />
          ))}
          {hoverLineX != null && (
            <line
              x1={hoverLineX}
              y1={layout.mt}
              x2={hoverLineX}
              y2={layout.mt + layout.plotH}
              stroke="rgba(255,255,255,.22)"
              strokeWidth={1}
            />
          )}
        </svg>

        {hoverDataX != null && hoverSamples.length > 0 && tooltipPos && (
          <ChartHoverTooltip left={tooltipPos.left} top={tooltipPos.top} width={190}>
            <ChartHoverHeader>Lap time: {formatChartX(hoverDataX, 'time')}</ChartHoverHeader>
            {hoverSamples.map(({ series: s, y }) => (
              <ChartHoverRow
                key={s.id}
                label={s.label}
                value={`${formatMetricValue(metric, y)} ${metric === 'cadence' && running ? t('runningMetrics.cadenceUnit') : METRIC_UNITS[metric]}`}
                color={s.color}
              />
            ))}
          </ChartHoverTooltip>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 6, fontSize: 11 }}>
        {series.map(s => (
          <span key={s.id} style={{ color: s.color, fontWeight: 600 }}>
            ● {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

const chartWrap: React.CSSProperties = {
  background: 'var(--surface-0)',
  borderRadius: 'var(--r-3)',
  boxShadow: 'var(--nm-raised-sm)',
  padding: 12,
};
